import * as Yup from "yup";

export const toolInitialValues = {
  name: "",
  description: "",
  parameters: {
    properties: [],
  },

  executionLogic: {
    type: "API_CALL",
    details: {
      url: "",
      method: "POST",
      headers: [],
      queryParams: [],
      body: "",
      authType: "NONE",
    },
  },
};

export const toolValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .min(3, "Tool name must be at least 3 characters long.")
    .max(50, "Tool name cannot exceed 50 characters.")
    .required("Tool name is required."),

  description: Yup.string()
    .trim()
    .min(10, "Description must be at least 10 characters long.")
    .max(250, "Description cannot exceed 250 characters.")
    .required("Tool description is required."),

  executionLogic: Yup.object({
    type: Yup.string().oneOf(["API_CALL"]).required(),
    details: Yup.object({
      url: Yup.string()
        .required("API URL is required.")
        .test(
          "is-valid-url-with-variables",
          "Must be a valid URL or URL with variables in {{variable}} format.",
          function (value) {
            if (!value) return false;
            const urlWithoutVariables = value.replace(
              /\{\{[^}]+\}\}/g,
              "placeholder"
            );
            try {
              new URL(urlWithoutVariables);
              return true;
            } catch (error) {
              return false;
            }
          }
        ),
      method: Yup.string().oneOf(["GET", "POST", "PUT", "PATCH", "DELETE"]),
      headers: Yup.array().of(
        Yup.object({
          key: Yup.string(),
          value: Yup.string(),
        })
      ),
      queryParams: Yup.array().of(
        Yup.object({
          key: Yup.string(),
          value: Yup.string(),
        })
      ),
      body: Yup.string().when("method", {
        is: (method) => ["POST", "PUT", "PATCH"].includes(method),
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.strip(),
      }),
      authType: Yup.string().default("NONE"),
    }),
  }),
});

export const newId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;
export const createKeyValue = () => ({ id: newId(), key: "", value: "" });
export const createProperty = () => ({
  id: newId(),
  key: "",
  type: "String",
  description: "",
  isRequired: false,
});
export const toSnakeCase = (value = "") => {
  if (!value) return "";
  return value
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase();
};

export const buildRequiredList = (properties = []) =>
  (Array.isArray(properties) ? properties : [])
    .filter((item) => item?.isRequired && item?.key)
    .map((item) => item.key);

export const copyToClipboard = async (text) => {
  if (typeof window === "undefined" || !text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.error("Clipboard API failed", error);
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const success = document.execCommand("copy");
    document.body.removeChild(textArea);
    return success;
  } catch (error) {
    console.error("Fallback clipboard copy failed", error);
    return false;
  }
};

export const parseCurlCommand = (curlString) => {
  if (!curlString || typeof curlString !== "string") return null;

  const args = curlString.trim().split(/\s+/);
  let url = "";
  let method = "GET";
  const headers = [];
  let body = "";
  const stripQuotes = (str) => {
    if (!str) return "";
    if (
      (str.startsWith('"') && str.endsWith('"')) ||
      (str.startsWith("'") && str.endsWith("'"))
    ) {
      return str.slice(1, -1);
    }
    return str;
  };

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];

    if (arg === "curl") continue;

    if (arg === "-X" || arg === "--request") {
      if (args[i + 1]) {
        method = args[i + 1].toUpperCase();
        i++;
      }
      continue;
    }

    if (arg === "-H" || arg === "--header") {
      let headerVal = args[i + 1];
      if (headerVal) {
        let j = i + 1;
        if (headerVal.startsWith("'") || headerVal.startsWith('"')) {
          const quoteChar = headerVal[0];
          let currentAcc = headerVal;
          if (
            headerVal.length > 1 &&
            headerVal.endsWith(quoteChar) &&
            !headerVal.slice(1, -1).includes(quoteChar)
          ) {
            i++;
          } else {
            j++;
            while (j < args.length) {
              currentAcc += " " + args[j];
              if (
                args[j].endsWith(quoteChar) &&
                (args[j].length > 1 ||
                  (args[j] === quoteChar && currentAcc.length > 2))
              ) {
                break;
              }
              j++;
            }
            headerVal = currentAcc;
            i = j;
          }
        } else {
          i++;
        }

        const cleanHeader = stripQuotes(headerVal);
        const match = cleanHeader.match(/^([^:]+):\s*(.*)$/);
        if (match) {
          headers.push({
            id: newId(),
            key: match[1].trim(),
            value: match[2].trim(),
          });
        }
      } else {
        i++;
      }
      continue;
    }

    if (
      arg === "-d" ||
      arg === "--data" ||
      arg === "--data-raw" ||
      arg === "--data-binary"
    ) {
      let bodyVal = args[i + 1];
      if (bodyVal) {
        let j = i + 1;
        if (bodyVal.startsWith("'") || bodyVal.startsWith('"')) {
          const quoteChar = bodyVal[0];
          let currentAcc = bodyVal;
          if (
            bodyVal.length > 1 &&
            bodyVal.endsWith(quoteChar) &&
            !bodyVal.slice(1, -1).includes(quoteChar)
          ) {
            i++;
          } else {
            j++;
            while (j < args.length) {
              currentAcc += " " + args[j];
              if (
                args[j].endsWith(quoteChar) &&
                (args[j].length > 1 ||
                  (args[j] === quoteChar && currentAcc.length > 2))
              ) {
                break;
              }
              j++;
            }
            bodyVal = currentAcc;
            i = j;
          }
        } else {
          i++;
        }
        body = stripQuotes(bodyVal);
        if (method === "GET") method = "POST";
      } else {
        i++;
      }
      continue;
    }

    if (
      arg.startsWith("http") ||
      arg.startsWith("'http") ||
      arg.startsWith('"http')
    ) {
      url = stripQuotes(arg);
    }
  }

  return {
    url,
    method,
    headers,
    body,
    queryParams: [],
    authType: "NONE",
  };
};
